import theme         from 'antd/lib/theme';
import { lighten }   from 'color2k';
import { ThemeMode } from '@widesk-ui/contexts/ThemeModeContext';

export const PRIMARY = '#0c9cdf';
// Thème clair : le bleu du logo (#46C0ED) assombri pour se lire sur blanc (contraste 5:1, contre
// 3:1 pour `PRIMARY`, gardé en thème sombre).
export const PRIMARY_LIGHT = '#0B74B5';
const PRIMARY_LIGHT_HOVER = '#095E93';

export const FONT_FAMILY = `'IBM Plex Sans', system-ui, -apple-system, 'Segoe UI', sans-serif`;

const NEUTRAL = {
	50 : '#fafafa',
	100: '#f5f5f5',
	200: '#e5e5e5',
	300: '#d4d4d4',
	400: '#a3a3a3',
	500: '#737373',
	600: '#525252',
	700: '#404040',
	800: '#262626',
	900: '#171717',
	950: '#0a0a0a',
} as const;

const SEMANTIC = {
	light: {
		primary        : PRIMARY_LIGHT,
		primaryForeground: NEUTRAL[50],
		background     : '#ffffff',
		card           : NEUTRAL[50],
		muted          : '#eef0f3',
		mutedForeground: '#5b6470',
		border         : '#c5ccd5',
		borderSecondary: '#e2e6eb',
	},
	dark: {
		primary        : PRIMARY,
		primaryForeground: NEUTRAL[50],
		background     : NEUTRAL[950],
		card           : NEUTRAL[900],
		muted          : NEUTRAL[800],
		mutedForeground: NEUTRAL[400],
		border         : NEUTRAL[700],
		borderSecondary: NEUTRAL[700],
	},
} as const;

export const NAV_BG = {
	light: '#001529',
	dark: NEUTRAL[900],
} as const;

export function getAntdTheme(mode: ThemeMode) {
	const isDark = mode === 'dark';
	const tokens = SEMANTIC[mode];

	return {
		algorithm: isDark ? theme.darkAlgorithm : theme.defaultAlgorithm,
		// Expose les tokens antd en vraies variables CSS (--ant-color-primary, --ant-color-border...)
		// pour que le SCSS brut (widesk-ui, TokamapEditor) puisse suivre le thème sans dupliquer de hex.
		cssVar   : true,
		token    : {
			fontFamily          : FONT_FAMILY,
			borderRadius        : 8,
			borderRadiusSM      : 6,
			borderRadiusLG      : 10,
			colorPrimary        : tokens.primary,
			colorBgLayout       : isDark ? tokens.background : tokens.muted,
			colorBgContainer    : tokens.card,
			colorBorder         : tokens.border,
			colorBorderSecondary: tokens.borderSecondary,
			colorTextDescription: tokens.mutedForeground,
			...(!isDark && {
				colorText          : '#111827',
				colorTextSecondary : '#3b4450',
				colorTextTertiary  : '#6b7480',
				colorFillQuaternary: '#f1f3f6',
				colorInfo          : PRIMARY_LIGHT,
				colorLink          : PRIMARY_LIGHT,
				colorLinkHover     : PRIMARY_LIGHT_HOVER,
				colorLinkActive    : PRIMARY_LIGHT_HOVER,
			}),
			...(isDark && {
				colorLink       : lighten(PRIMARY, 0.14),
				colorLinkHover  : lighten(PRIMARY, 0.24),
				colorLinkActive : PRIMARY,
				colorBgElevated : tokens.muted,
			}),
		},
		components: {
			Button: {
				fontWeight: 600,
				...(isDark && {
					dangerShadow : 'none',
					defaultShadow: 'none',
					primaryShadow: 'none',
				}),
			},
			Layout: {
				siderBg  : NAV_BG[mode],
				headerBg : NAV_BG[mode],
				triggerBg: NAV_BG[mode],
			},
			Menu: {
				darkItemBg : NAV_BG[mode],
				darkPopupBg: NAV_BG[mode],
			},
			Slider: {
				railSize            : 4,
				railBg              : tokens.border,
				railHoverBg         : isDark ? NEUTRAL[600] : NEUTRAL[300],
				trackBg             : tokens.primary,
				trackHoverBg        : tokens.primary,
				handleColor         : tokens.primary,
				handleActiveColor   : tokens.primary,
			},
		},
		hashed: false,
	};
}
